/**
 * Supabase client — single source of truth for auth.
 *
 * - On Capacitor native, we want sessions to survive app restarts via the
 *   device's secure preferences (not localStorage, which can be cleared by
 *   the WebView in some scenarios). On the web build we never hit this path
 *   because the web is view-only — no login UI — but the client must still
 *   instantiate cleanly.
 */
import { createClient } from '@supabase/supabase-js';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Async storage adapter backed by @capacitor/preferences on native and
// localStorage on web. Supabase-js accepts async storage transparently.
const capacitorStorage = {
  async getItem(key) {
    const { value } = await Preferences.get({ key });
    return value;
  },
  async setItem(key, value) {
    await Preferences.set({ key, value });
  },
  async removeItem(key) {
    await Preferences.remove({ key });
  },
};

const isNative = Capacitor.isNativePlatform();

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    storage: isNative ? capacitorStorage : (typeof window !== 'undefined' ? window.localStorage : undefined),
    storageKey: 'foresight_supabase_auth',
    autoRefreshToken: true,
    persistSession: true,
    // Native opens OAuth in the system browser and returns via deep link —
    // we parse the URL ourselves and call setSession. The web build doesn't
    // have a login flow at all, so this stays off everywhere.
    detectSessionInUrl: false,
    flowType: 'implicit',
  },
});
